import { useState } from 'react';

interface UrlInputProps {
  onScrape: (url: string, includeImages: boolean) => void;
  isLoading: boolean;
}

export function UrlInput({ onScrape, isLoading }: UrlInputProps) {
  const [url, setUrl] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [includeImages, setIncludeImages] = useState(true);
  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (url.trim()) {
      onScrape(url.trim(), includeImages);
    }
  };

  const isWildcard = url.trim().endsWith('*');

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <label htmlFor="url-input" className="block text-sm font-medium text-surface-300 mb-3">
        Enter a URL to transform
      </label>

      <div className="flex gap-3">
        <div className={`flex-1 relative rounded-xl transition-all duration-300 ${
          isFocused ? 'ring-2 ring-primary-500/30' : ''
        }`}>
          {/* URL icon */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-500">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
          </div>

          <input
            id="url-input"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="https://example.com/docs/*"
            className="input-field pl-12 pr-4"
            disabled={isLoading}
          />

          {/* Wildcard indicator */}
          {isWildcard && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <span className="px-2 py-1 bg-primary-500/20 text-primary-400 text-xs font-medium rounded-md border border-primary-500/30">
                Multi-page
              </span>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading || !url.trim()}
          className="btn-primary flex items-center gap-2.5 whitespace-nowrap"
        >
          {isLoading ? (
            <>
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>Transforming...</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
              <span>Transform</span>
            </>
          )}
        </button>
      </div>

      <div className="flex items-center justify-between mt-4">
        {/* Include images checkbox */}
        <label className="flex items-center gap-3 cursor-pointer group">
          <div className="relative">
            <input
              type="checkbox"
              checked={includeImages}
              onChange={(e) => setIncludeImages(e.target.checked)}
              className="sr-only peer"
              disabled={isLoading}
            />
            <div className={`w-5 h-5 rounded-md border-2 transition-all duration-200 flex items-center justify-center ${
              includeImages
                ? 'bg-primary-500 border-primary-500'
                : 'border-surface-500 group-hover:border-surface-400'
            } ${isLoading ? 'opacity-50' : ''}`}>
              {includeImages && (
                <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <span className={`text-sm transition-colors ${
            isLoading ? 'text-surface-500' : 'text-surface-400 group-hover:text-surface-300'
          }`}>
            Include images in markdown
          </span>
        </label>

        {/* Help tooltip */}
        <div className="relative">
          <button
            type="button"
            className="flex items-center gap-2 text-sm text-surface-500 hover:text-surface-400 transition-colors"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={() => setShowTooltip(!showTooltip)}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>Multi-page scraping</span>
          </button>

          {showTooltip && (
            <div className="absolute right-0 top-full mt-2 p-4 bg-surface-800 border border-surface-700 rounded-xl shadow-xl z-20 max-w-sm animate-fade-in">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-surface-200 mb-1">Multi-page mode</p>
                  <p className="text-sm text-surface-400 leading-relaxed">
                    Add <code className="px-1.5 py-0.5 bg-surface-700 text-primary-400 rounded text-xs font-mono">*</code> at the end of the URL to transform all pages under that path.
                  </p>
                  <p className="text-sm text-surface-500 mt-2">
                    Example: <code className="px-1.5 py-0.5 bg-surface-700 text-surface-300 rounded text-xs font-mono">https://docs.example.com/*</code>
                  </p>
                </div>
              </div>
              {/* Arrow */}
              <div className="absolute -top-1.5 right-6 w-3 h-3 bg-surface-800 border-l border-t border-surface-700 transform rotate-45" />
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
